import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PALS, PAL_BY_SLUG, ELEMENT_COLORS } from "@/lib/pals-data";
import { getBreedingChild, getBreedingParents } from "@/lib/pals-extras";
import { PalPortrait } from "@/components/PalPortrait";
import { FaqSection, RelatedGuides, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, toolSchema, snippetAnswerSchema } from "@/lib/schema";
import { ClusterLinks } from "@/components/ClusterLinks";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { SNIPPETS } from "@/lib/snippet-copy";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";
import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";
import { ShareButton } from "@/components/ShareButton";

/** Fixed set of high-interest breeding targets, rendered as plain HTML on the server. */
const STATIC_COMBOS = PALS.filter((p) => p.rarity === "Legendary" || p.rarity === "Rare")
  .slice(0, 12)
  .map((target) => {
    const pair = getBreedingParents(target, 1)[0];
    return pair ? { target, a: pair.a, b: pair.b } : null;
  })
  .filter((r): r is { target: typeof PALS[number]; a: typeof PALS[number]; b: typeof PALS[number] } => Boolean(r));

const FAQ: FaqItem[] = [
  {
    q: "How does breeding work in Palworld?",
    a: "Place two Pals of opposite gender in a Breeding Farm with Cake in the chest. They produce an egg whose species is decided by the average of both parents' internal breed power, then incubated in an Egg Incubator.",
  },
  {
    q: "How do I breed a specific Pal in Palworld?",
    a: "Pick your target in the reverse lookup above and the calculator lists parent pairs whose combined breed power lands on that Pal. Any listed pair works, so use whichever two Pals you already own.",
  },
  {
    q: "Do passive skills pass to the baby Pal?",
    a: "Yes. Each baby inherits passives from the pool of both parents, so stacking Legend, Musclehead, Ferocious and Swift on the parents is the fastest way to roll a four-passive combat Pal.",
  },
  {
    q: "Does the parents' gender or level change the egg?",
    a: "No. Gender only decides whether a pair can breed at all, and level has no effect on the resulting species. Only the two species and their breed power matter.",
  },
  {
    q: "What do I need to keep a Breeding Farm running?",
    a: "A Breeding Farm, a steady Cake supply (wheat, milk, eggs, honey and berries) and an Egg Incubator. Assign a Pal with the Farming or Gathering suitability to keep ingredient production up.",
  },
  {
    q: "How to breed Anubis Palworld step by step?",
    a: "Catch Penking and Bushi, place one male and one female in a Breeding Farm, add Cake to the farm box, incubate the egg, then repeat with stronger passives until Anubis inherits the traits you want.",
  },
  {
    q: "What do you get when you breed Relaxaurus and Sparkit?",
    a: "Use the Parents to Child calculator above: select Relaxaurus as Parent A and Sparkit as Parent B, and the result card updates instantly with the Pal produced by that exact pair.",
  },
  {
    q: "Palworld shortest breeding path calculator?",
    a: "The reverse lookup works as a shortest breeding route calculator Palworld players can use to find the quickest available parent pair for a target species, then chain that result into passive inheritance planning.",
  },
  {
    q: "Palworld reverse breeding calculator tool?",
    a: "Pick any target Pal and the Palworld reverse breeding lookup parents finder lists valid parent combinations instead of making you test pairs one by one.",
  },
  {
    q: "Katress Palworld breeding combinations and work suitability?",
    a: "Search Katress in the target picker to see breeding combinations, then open Katress's Pal page for work suitability, partner skill, drops and stat data.",
  },
  {
    q: "How to breed specific sub type combinations Palworld?",
    a: "For officially confirmed subspecies, choose the subtype target in reverse lookup and preserve the desired passive pool through each parent generation before hatching the final egg.",
  },
  {
    q: "Passive trait inheritance chain breeding calculator and Palworld passive skill chain planner tools?",
    a: "Use the calculator as a passive skill chain planner: find a compatible pair, hatch the child with one or two target passives, then use that child as the next parent until the final Pal carries all four traits.",
  },
  {
    q: "Is this the same as a Pal World breeding calculator or breeding combo chart?",
    a: "Yes — Pal World and Palworld are the same game, and this page replaces static breeding combo charts. Instead of scrolling a table you select the two parents, or the child you want, and the valid combinations are listed for you.",
  },
  {
    q: "Does the Palworld breeding calculator cover every Pal in the game?",
    a: "Every Pal in the Pal Stars database can be used as a parent or chosen as a breeding target, including legendary Pals and officially confirmed subspecies, so the calculator covers the full roster rather than a popular-Pals shortlist.",
  },
];

export const Route = createFileRoute("/breeding")({
  head: () => ({
    meta: [
      { title: "Palworld Breeding Calculator — Parents & Routes" },
      { name: "description", content: "Find Palworld parent combos, reverse breeding routes, Anubis steps, passive inheritance chains and subtype breeding paths." },
      { property: "og:title", content: "Palworld Breeding Calculator" },
      { property: "og:description", content: "Every parent pairing for every Pal, instantly." },
        { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/breeding" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Palworld Breeding Calculator" },
        { name: "twitter:description", content: "Every parent pairing for every Pal, instantly." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/breeding" }],
    scripts: [faqSchema(FAQ, "https://palstars.com/breeding"), breadcrumbSchema([["Home", "/"], ["Breeding Calculator", "/breeding"]]), toolSchema({ name: "Palworld Breeding Calculator", description: "Find every parent combination for every Palworld Pal, with pairing probabilities.", path: "/breeding" }), snippetAnswerSchema({ question: SNIPPETS.breeding.question, answer: SNIPPETS.breeding.answer, path: "/breeding" })],
  }),
  component: BreedingPage,
});


function BreedingPage() {
  const [aSlug, setA] = useState(PALS[0].slug);
  const [bSlug, setB] = useState(PALS[5].slug);
  const [targetSlug, setTarget] = useState(PALS[10].slug);

  const a = PAL_BY_SLUG[aSlug];
  const b = PAL_BY_SLUG[bSlug];
  const target = PAL_BY_SLUG[targetSlug];
  const child = useMemo(() => getBreedingChild(a, b), [a, b]);
  const parents = useMemo(() => getBreedingParents(target, 12), [target]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Breeding Calculator</h1>
        <ShareButton title={"Palworld Breeding Calculator — Pal Stars"} text={"Palworld breeding calculator on Pal Stars."} path="/breeding" />
      </div>
      <p className="aeo-answer text-muted-foreground mt-2 max-w-3xl">
        This Palworld breeding calculator works both ways: pair any two Pals to see the exact child that egg hatches, or
        pick a target Pal and it lists every parent combination that produces it. Results come from each Pal's breed
        power, the same value the game averages between the two parents, so no cakes are wasted on guesswork.
      </p>

      <SnippetFunnel className="mt-6" {...SNIPPETS.breeding} />
      <ContextVisualStrip variant="breeding" />
      <p className="mt-3 text-sm">
        Optimising offspring stats?{" "}
        <Link to="/guides/$slug" params={{ slug: "passive-skill-inheritance" }} className="text-primary hover:underline">
          Read the Palworld breeding guide to passive skill inheritance
        </Link>{" "}
        — how Legend, Musclehead, Ferocious and Swift pass to the next generation.
      </p>

      <section className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-lg font-semibold mb-4">Parents → Child</h2>
        <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center">
          <ParentPicker value={aSlug} onChange={setA} label="Parent A" />
          <div className="text-3xl text-muted-foreground text-center">+</div>
          <ParentPicker value={bSlug} onChange={setB} label="Parent B" />
          <div className="text-3xl text-muted-foreground text-center">=</div>
          <div className="text-center">
            <Link to="/pals/$slug" params={{ slug: child.slug }} className="glass rounded-xl p-3 card-hover block">
              <PalPortrait pal={child} size={160} />
              <div className="mt-2 font-semibold">{child.name}</div>
              <div className="text-xs" style={{ color: ELEMENT_COLORS[child.element] }}>{child.element} · {child.rarity}</div>
            </Link>
          </div>
        </div>
      </section>

      <section className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-lg font-semibold mb-4">Reverse lookup: parents for a target</h2>
        <label className="block text-sm max-w-md">
          <span className="text-muted-foreground">Target Pal</span>
          <select value={targetSlug} onChange={(e) => setTarget(e.target.value)}
            aria-label="Target Pal for reverse breeding lookup"
            className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2 text-sm">
            {PALS.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
          </select>
        </label>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {parents.map((pair, i) => (
            <div key={i} className="rounded-xl border p-3 bg-background/40 flex items-center gap-3 min-w-0">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="shrink-0"><PalPortrait pal={pair.a} size={48} /></div>
                <div className="text-sm min-w-0 flex-1">
                  <Link to="/pals/$slug" params={{ slug: pair.a.slug }} className="hover:underline font-medium block truncate">{pair.a.name}</Link>
                  <div className="text-[10px]" style={{ color: ELEMENT_COLORS[pair.a.element] }}>{pair.a.element}</div>
                </div>
              </div>
              <div className="text-muted-foreground shrink-0">+</div>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="shrink-0"><PalPortrait pal={pair.b} size={48} /></div>
                <div className="text-sm min-w-0 flex-1">
                  <Link to="/pals/$slug" params={{ slug: pair.b.slug }} className="hover:underline font-medium block truncate">{pair.b.name}</Link>
                  <div className="text-[10px]" style={{ color: ELEMENT_COLORS[pair.b.element] }}>{pair.b.element}</div>
                </div>
              </div>
            </div>

          ))}
        </div>
      </section>

      {/* Static, server-rendered combo table so crawlers get real breeding data
          without running the interactive pickers above. */}
      <section className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-lg font-semibold">Popular Palworld breeding combinations</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          A fixed reference table of parent pairs for the most-searched Palworld breeding targets. The calculator above
          covers every remaining Pal in the roster.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm border-collapse">
            <caption className="sr-only">Palworld breeding combinations: target Pal and two parent Pals that hatch it.</caption>
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                <th scope="col" className="px-3 py-2 text-left font-medium">Target Pal</th>
                <th scope="col" className="px-3 py-2 text-left font-medium">Element</th>
                <th scope="col" className="px-3 py-2 text-left font-medium">Parent A</th>
                <th scope="col" className="px-3 py-2 text-left font-medium">Parent B</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {STATIC_COMBOS.map((row) => (
                <tr key={row.target.slug} className="hover:bg-card">
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    <Link to="/pals/$slug" params={{ slug: row.target.slug }} className="hover:underline">
                      {row.target.name}
                    </Link>
                  </th>
                  <td className="px-3 py-2" style={{ color: ELEMENT_COLORS[row.target.element] }}>{row.target.element}</td>
                  <td className="px-3 py-2">
                    <Link to="/pals/$slug" params={{ slug: row.a.slug }} className="hover:underline">{row.a.name}</Link>
                  </td>
                  <td className="px-3 py-2">
                    <Link to="/pals/$slug" params={{ slug: row.b.slug }} className="hover:underline">{row.b.name}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CrossCategoryLinks side="game" />

      <FaqSection items={FAQ} heading="Palworld breeding FAQ" />
      <ClusterLinks hub="/breeding" heading="Palworld breeding calculator guides" />
      <RelatedGuides
        links={[
          { slug: "palworld-breeding-guide", label: "Palworld Breeding Guide — Combos, Cakes & Eggs" },
          { slug: "breeding-mechanics", label: "Breed Power, Cakes & Ranch Optimization" },
          { slug: "passive-skill-inheritance", label: "Passive Skill Inheritance Guide" },
          { slug: "palworld-egg-guide", label: "Palworld Egg Guide & FAQ" },
        ]}
      />
    </div>
  );
}


function ParentPicker({ value, onChange, label }: { value: string; onChange: (s: string) => void; label: string }) {
  const p = PAL_BY_SLUG[value];
  return (
    <div className="text-center">
      <div className="text-xs text-muted-foreground mb-2">{label}</div>
      <div className="glass rounded-xl p-3">
        <PalPortrait pal={p} size={140} />
        <select value={value} onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          className="mt-2 w-full rounded-md border bg-background/50 px-2 py-1 text-sm">
          {PALS.map((pp) => <option key={pp.id} value={pp.slug}>{pp.name}</option>)}
        </select>
      </div>
    </div>
  );
}
